import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  Checkbox,
  Dropdown,
  RadioButtonGroup,
  RadioButton,
} from "@carbon/react";
import dayjs from "dayjs";
import useMediaQuery from "../components/useMediaQuery";
import AIInterviewTable from "../components/AIInterviewTable";
import LabelCheckboxGroup from "../components/LabelCheckboxGroup";
import api from "../api";
import {
  QUESTION_LABELS,
} from "../components/aiInterviewUtils";
import "./AiInterviewQuestions.css";
import { useAuth } from "../auth/AuthContext";

const PERIOD_OPTIONS = [
  { label: "1ヶ月", value: 1 },
  { label: "3ヶ月", value: 3 },
  { label: "6ヶ月", value: 6 },
  { label: "12ヶ月", value: 12 },
  { label: "全期間", value: 999 },
];

const MODE = {
  PERIOD: "period",
  DATES: "dates",
};

export default function AiInterviewQuestions() {
  const { state } = useLocation();
  const aiDatesFromSummary = state?.aiDates ?? []; // Summaryから受け取る日付群
  const isNarrow = useMediaQuery("(max-width: 1024px)");

  // ステート
  const [mode, setMode] = useState(MODE.DATES);
  const [availableDates, setAvailableDates] = useState([]); // 降順
  const [selectedPeriod, setSelectedPeriod] = useState(3);
  const [manualDates, setManualDates] = useState([]); // チェックされた日付群
  const [rowsAll, setRowsAll] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState(Object.values(QUESTION_LABELS));
  const [loading, setLoading] = useState(false);

  const { user } = useAuth() || {};
  const storedPatient = sessionStorage.getItem("patient");
  const patient = storedPatient ? JSON.parse(storedPatient) : null;
  const research_id = patient?.research_id || user?.id || "P010";

  // === ヘルパー: 指定月数以内の日付抽出 ===
  const makeDatesForPeriod = useCallback((datesDesc, months) => {
    if (!datesDesc?.length) return [];
    if (months === 999) return datesDesc;
    const latest = dayjs(datesDesc[0]);
    const from = latest.subtract(months, "month");
    const inPeriod = datesDesc.filter((d) => {
      const dd = dayjs(d);
      return dd.isSame(from, "day") || dd.isAfter(from);
    });
    return inPeriod.length ? inPeriod : [datesDesc[0]];
  }, []);

  // === 初回ロード: Summary経由のデータで日付リストをセット ===
  useEffect(() => {
    const sorted = [...(aiDatesFromSummary.length ? aiDatesFromSummary : 
      JSON.parse(sessionStorage.getItem("aiDates") ?? "[]"))].sort(
      (a, b) => dayjs(b).valueOf() - dayjs(a).valueOf()
    );
    setAvailableDates(sorted);
    if (sorted.length > 0) setManualDates([sorted[0]]);
  }, [aiDatesFromSummary]);

  // === API呼び出し共通関数 ===
  const fetchAiDialog = useCallback(
    async (datesToFetch) => {
      if (!datesToFetch?.length) {
        setRowsAll([]);
        return;
      }

      try {
        setLoading(true);
        const res = await api.post("/v1/doctor/ai_dialog", {
          research_id,
          category_list: Object.keys(QUESTION_LABELS),
          request_date_list: datesToFetch,
        });

        const data = res.data ?? {};
        const {
          answer_date_list = [],
          question_list = [],
          answer_list = [],
          regenerate_list = [],
          subid_list = [],
        } = data;

        // 各配列を1行ずつまとめる
        const rows = question_list.map((q, i) => {
          const categoryId = Object.keys(QUESTION_LABELS)[i] || "UNKNOWN";
          return {
            date: answer_date_list[i] ?? datesToFetch[0],
            label: QUESTION_LABELS[categoryId] ?? categoryId,
            category: categoryId,
            question: q,
            baseQuestion: `subid:${subid_list[i] ?? ""}`,
            score: answer_list[i] ?? "",
            regenerateCount: regenerate_list[i] ?? 0,
          };
        });

        setRowsAll(rows);
      } catch (err) {
        console.error("ai_dialog取得エラー:", err);
      } finally {
        setLoading(false);
      }
    },
    [research_id]
  );

  // === mode/期間/日付変更で再取得 ===
  useEffect(() => {
    if (!availableDates.length) return;

    if (mode === MODE.PERIOD) {
      const dates = makeDatesForPeriod(availableDates, selectedPeriod);
      fetchAiDialog(dates);
    } else if (mode === MODE.DATES) {
      fetchAiDialog(manualDates);
    }
  }, [mode, selectedPeriod, manualDates, availableDates, fetchAiDialog, makeDatesForPeriod]);

  // === フィルタリング（ラベル選択） ===
  const filtered = useMemo(() => {
    if (!rowsAll.length) return [];
    const labelSet = new Set(selectedLabels);
    return rowsAll.filter((r) => labelSet.has(r.label));
  }, [rowsAll, selectedLabels]);

  return (
    <div style={{ margin: "0 auto", maxWidth: "1000px" }}>
      <h1>AI問診結果：質問</h1>

      {/* === モード切替 === */}
      <div
        style={{
          margin: "1rem 0",
          padding: "1rem",
          border: "1px solid #ccc",
          borderRadius: "4px",
        }}
      >
        <RadioButtonGroup
          className="aiq-mode-inline"
          name="aiq-mode"
          valueSelected={mode}
          onChange={setMode}
          orientation="horizontal"
        >
          <RadioButton id="mode-period" value={MODE.PERIOD} labelText="期間で選ぶ" />
          <RadioButton id="mode-dates" value={MODE.DATES} labelText="日付を個別に選ぶ" />
        </RadioButtonGroup>

        {/* === 期間モード === */}
        {mode === MODE.PERIOD && (
          <div style={{ marginTop: "0.75rem" }}>
            <Dropdown
              id="period-select"
              label={
                PERIOD_OPTIONS.find((opt) => opt.value === selectedPeriod)?.label ??
                ""
              }
              items={PERIOD_OPTIONS}
              itemToString={(item) => item?.label ?? ""}
              selectedItem={PERIOD_OPTIONS.find(
                (opt) => opt.value === selectedPeriod
              )}
              onChange={({ selectedItem }) =>
                selectedItem && setSelectedPeriod(selectedItem.value)
              }
            />
            <div
              style={{
                marginTop: "0.5rem",
                fontSize: "0.85rem",
                color: "#6f6f6f",
              }}
            >
              ※ 選択した期間に含まれる日付が自動的に反映されます。
            </div>
          </div>
        )}

        {/* === 日付個別モード === */}
        {mode === MODE.DATES && (
          <div style={{ marginTop: "0.75rem" }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.5rem",
              }}
            >
              {availableDates.map((d) => (
                <Checkbox
                  key={d}
                  id={d}
                  labelText={d}
                  checked={manualDates.includes(d)}
                  onChange={() =>
                    setManualDates((prev) =>
                      prev.includes(d)
                        ? prev.filter((x) => x !== d)
                        : [...prev, d]
                    )
                  }
                />
              ))}
            </div>
            <div
              style={{
                marginTop: "0.5rem",
                fontSize: "0.85rem",
                color: "#6f6f6f",
              }}
            >
              ※ チェックした日付のみ表示します。
            </div>
          </div>
        )}
      </div>

      {/* === カテゴリフィルタ === */}
      <div style={{ margin: "0.5rem 0 1rem 0" }}>
        <LabelCheckboxGroup
          allLabels={Object.values(QUESTION_LABELS)}
          selectedLabels={selectedLabels}
          onToggle={(label) =>
            setSelectedLabels((prev) =>
              prev.includes(label)
                ? prev.filter((l) => l !== label)
                : [...prev, label]
            )
          }
          idPrefix="recent"
        />
      </div>

      {/* === テーブル === */}
      {loading ? (
        <p style={{ textAlign: "center", color: "#6f6f6f" }}>読み込み中...</p>
      ) : (
        <AIInterviewTable
          rows={filtered}
          latestOnly={false}
          showRegenCount
          showBaseQuestion
          expandable
          isNarrow={isNarrow}
        />
      )}
    </div>
  );
}
